import type { DocumentFilters } from "@/stores/documents/DocumentStore";
import type { AvailableFilters } from "@/stores/documents/repository";

/**
 * Filter ID for the "Verified only" toggle. Unlike the four tag dimensions this
 * is a single flag with no value list, but it rides in the same
 * `activeTagFilters` array so it inherits the shared filter state, the
 * removable chip, and Clear all for free.
 */
export const VERIFIED_FILTER_ID = "verified";

/**
 * Parse UI tag filter IDs into DocumentFilters for database queries.
 *
 * Filter ID formats:
 * - `verified` - the Verified-only toggle (no value, presence means "on")
 * - `project-{project_id}` - numeric project ID
 * - `category-{category_name_lowercase}` - category name (needs lookup for ID)
 * - `filter-group-{group_id}` - filter group (includes linked category + all item categories)
 * - `filter-item-{item_id}` - filter group item (specific category) - legacy format
 * - `filter-merged-{group_id}-{normalized_name}` - merged filter items (multiple categories with same name)
 * - `construction-{discipline_id}` - numeric design discipline ID
 * - `stage-{stage_id}` - numeric design stage ID
 *
 * @param activeFilters - Array of filter IDs from the UI (e.g., ['project-123', 'stage-5'])
 * @param availableFilters - Pre-fetched available filters for ID lookups
 * @param baseFilters - Optional base filters to merge with (e.g., states)
 * @returns DocumentFilters object for the store/repository
 */
export function parseTagFilters(
  activeFilters: string[],
  availableFilters: AvailableFilters | null,
  baseFilters?: Partial<DocumentFilters>
): DocumentFilters {
  const filters: DocumentFilters = { ...baseFilters };

  const normalizeCategoryName = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/^drawing\s+/, "");

  const normalizeMergedTagSlug = (value: string) => value.trim().toLowerCase().replace(/\s+/g, "-");

  if (activeFilters.length === 0) {
    return filters;
  }

  // Verified toggle. Set only when on, so an untouched toggle leaves the query
  // exactly as it was rather than sending an explicit `false`.
  if (activeFilters.includes(VERIFIED_FILTER_ID)) {
    filters.verifiedOnly = true;
  }

  // Group filters by type
  const projectFilters = activeFilters.filter((f) => f.startsWith("project-"));
  const categoryFilters = activeFilters.filter((f) => f.startsWith("category-"));
  const filterGroupFilters = activeFilters.filter((f) => f.startsWith("filter-group-"));
  const filterItemFilters = activeFilters.filter((f) => f.startsWith("filter-item-"));
  const filterMergedFilters = activeFilters.filter((f) => f.startsWith("filter-merged-"));
  const disciplineFilters = activeFilters.filter((f) => f.startsWith("construction-"));
  const stageFilters = activeFilters.filter((f) => f.startsWith("stage-"));

  // Parse project IDs (numeric)
  if (projectFilters.length > 0) {
    filters.projectIds = projectFilters
      .map((f) => {
        const idStr = f.replace("project-", "");
        return parseInt(idStr, 10);
      })
      .filter((id) => !isNaN(id));
  }

  // Collect category IDs from all category-related filters
  const collectedCategoryIds = new Set<number>();

  // Parse filter group tags (legacy/backward compatibility only)
  // NOTE: filter-group-* IDs are no longer added to activeFilters.
  // Parent checkboxes now only toggle children, so only filter-merged-* IDs are used.
  // This code is kept for backward compatibility with old URLs that may have filter-group-* IDs.
  if (
    (filterGroupFilters.length > 0 || filterItemFilters.length > 0) &&
    availableFilters?.filterGroups
  ) {
    const filterGroupIds = filterGroupFilters.map((f) =>
      parseInt(f.replace("filter-group-", ""), 10)
    );
    const filterItemIds = filterItemFilters.map((f) => parseInt(f.replace("filter-item-", ""), 10));

    for (const group of availableFilters.filterGroups) {
      // If this group is selected (legacy URL), add all item categories
      if (filterGroupIds.includes(group.id)) {
        for (const item of group.items) {
          collectedCategoryIds.add(item.documentCategoryId);
        }
      } else {
        // Check if any items from this group are selected (legacy format)
        for (const item of group.items) {
          if (filterItemIds.includes(item.id)) {
            collectedCategoryIds.add(item.documentCategoryId);
          }
        }
      }
    }
  }

  // Parse merged filter tags (deduplicated items with same name)
  // Format: filter-merged-{groupId}-{normalized-name}
  if (filterMergedFilters.length > 0 && availableFilters?.filterGroups) {
    for (const mergedTag of filterMergedFilters) {
      // Extract groupId and slug from tag ID
      const match = mergedTag.match(/^filter-merged-(\d+)-(.+)$/);
      if (!match) continue;

      const groupId = parseInt(match[1], 10);
      const mergedTagSlug = match[2];

      const group = availableFilters.filterGroups.find((g) => g.id === groupId);
      if (!group) continue;

      // Match by canonical slug so hyphenated names (e.g. E-Mail) stay unambiguous
      for (const item of group.items) {
        if (normalizeMergedTagSlug(item.categoryName) === mergedTagSlug) {
          collectedCategoryIds.add(item.documentCategoryId);
        }
      }
    }
  }

  // Parse legacy category tags (old format - still supported for backward compatibility)
  if (categoryFilters.length > 0 && availableFilters) {
    const categoryNames = categoryFilters.map((f) =>
      normalizeCategoryName(f.replace("category-", ""))
    );

    // Find directly selected categories
    const selectedCategories = availableFilters.categories.filter((cat) =>
      categoryNames.includes(normalizeCategoryName(cat.name))
    );

    const categoryById = new Map(availableFilters.categories.map((c) => [c.id, c]));

    // Expand to include all child category IDs.
    // Use an explicit stack (not recursion) + a single visited set to ensure we can't
    // overflow the call stack or loop forever if the category graph is cyclic.
    const MAX_CATEGORY_TRAVERSAL_DEPTH = 50;

    function addCategoryAndChildren(rootId: number) {
      const stack: Array<{ id: number; depth: number }> = [{ id: rootId, depth: 0 }];

      while (stack.length > 0) {
        const node = stack.pop();
        if (!node) break;

        if (collectedCategoryIds.has(node.id)) continue;
        collectedCategoryIds.add(node.id);

        // Safety guard for corrupted / unexpectedly deep category trees
        if (node.depth >= MAX_CATEGORY_TRAVERSAL_DEPTH) continue;

        const category = categoryById.get(node.id);
        if (!category?.childCategoryIds || category.childCategoryIds.length === 0) continue;

        for (const childId of category.childCategoryIds) {
          if (!collectedCategoryIds.has(childId)) {
            stack.push({ id: childId, depth: node.depth + 1 });
          }
        }
      }
    }

    // Start with directly selected categories and expand
    for (const cat of selectedCategories) {
      addCategoryAndChildren(cat.id);
    }
  }

  // Set categoryIds if we collected any
  if (collectedCategoryIds.size > 0) {
    filters.categoryIds = Array.from(collectedCategoryIds);
  }

  // Parse design discipline IDs (numeric)
  if (disciplineFilters.length > 0) {
    filters.designDisciplineIds = disciplineFilters
      .map((f) => {
        const idStr = f.replace("construction-", "");
        return parseInt(idStr, 10);
      })
      .filter((id) => !isNaN(id));
  }

  // Parse design stage IDs (numeric)
  if (stageFilters.length > 0) {
    filters.designStageIds = stageFilters
      .map((f) => {
        const idStr = f.replace("stage-", "");
        return parseInt(idStr, 10);
      })
      .filter((id) => !isNaN(id));
  }

  return filters;
}

/**
 * Check if any M2M filters are active.
 * Used to determine if we should skip client-side filtering (since server handles it).
 *
 * NOTE: filter-group-* is kept for backward compatibility but is no longer added to activeFilters.
 * Parent checkboxes now only toggle children (filter-merged-* IDs).
 */
export function hasActiveM2MFilters(activeFilters: string[]): boolean {
  return activeFilters.some(
    (f) =>
      f.startsWith("project-") ||
      f.startsWith("category-") ||
      f.startsWith("filter-group-") || // Legacy/backward compatibility
      f.startsWith("filter-item-") || // Legacy/backward compatibility
      f.startsWith("filter-merged-") ||
      f.startsWith("construction-") ||
      f.startsWith("stage-")
  );
}
